import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, Circle, Clock } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { PageLoader } from '../components/ui/Spinner';
import IncidentMap from '../components/Incident/IncidentMap';
import CommandCenterService from '../services/CommandCenterService';
import AnnouncementMemberService from '../services/AnnouncementMemberService';
import { buildRoster } from '../utils/utils';

const STATUS_CHOICES = [
  { key: 'green', label: 'All Clear / Safe', cls: 'border-green-500 bg-green-50 text-green-700' },
  { key: 'amber', label: 'Engaged / Busy', cls: 'border-orange-400 bg-orange-50 text-orange-700' },
  { key: 'red', label: 'Critical / Help Needed', cls: 'border-red-500 bg-red-50 text-red-700' },
];

const EQUIPMENT_ITEMS = [
  { key: 'ppe', label: 'PPE' },
  { key: 'dosemeter', label: 'Dosemeter' },
  { key: 'consumables', label: 'Consumables' },
];

const STATUS_DOT_CLS = { Accepted: 'bg-green-500', Rejected: 'bg-red-500' };

function formatElapsed(startIso, nowMs) {
  const totalSeconds = Math.max(0, Math.floor((nowMs - new Date(startIso).getTime()) / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
}

function ChecklistChip({ label, checked, disabled, onClick }) {
  const Icon = checked ? CheckCircle2 : Circle;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
        checked ? 'bg-green-50 border-green-300 text-green-700' : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:border-neutral-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

export default function TeamLeaderView() {
  const [modal, setModal] = useState(null); // 'teamStatus' | 'resource' | 'broadcast'
  const [incident, setIncident] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rawMembers, setRawMembers] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);

  const closeAndToast = (message) => {
    setModal(null);
    toast.success(message);
  };

  useEffect(() => {
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickId);
  }, []);

  const patchMember = async (id, uiPatch, apiPatch) => {
    setRoster((prev) => prev.map((m) => (m.id === id ? { ...m, ...uiPatch } : m)));
    try {
      await AnnouncementMemberService.update(id, apiPatch);
    } catch (error) {
      console.log(error);
      toast.error('Failed to save update. Please retry.');
    }
  };

  const toggleRoleConfirmed = (member) => {
    if (!member.hasRole) return;
    const roleConfirmed = !member.roleConfirmed;
    patchMember(member.id, { roleConfirmed }, { roleConfirmed });
  };

  const toggleEquipmentItem = (member, key) => {
    const equipmentChecklist = { ...member.equipmentChecklist, [key]: !member.equipmentChecklist[key] };
    patchMember(member.id, { equipmentChecklist }, { equipmentChecklist });
  };

  const markActive = (member) => {
    const activeSince = new Date().toISOString();
    patchMember(member.id, { activeSince }, { ata: activeSince });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const commandResponse = await CommandCenterService.getStats();
        const activeIncident = commandResponse?.data?.activeIncident || null;
        setIncident(activeIncident);

        if (activeIncident) {
          const { entity } = await AnnouncementMemberService.findById(activeIncident.id);
          setRoster(buildRoster(entity || [], activeIncident.lat, activeIncident.lng));
          setRawMembers(entity || []);
        } else {
          setRoster([]);
          setRawMembers([]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    load().finally(() => setLoading(false));
    const intervalId = setInterval(load, 15000);
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Sidebar title="Team Leader">
        <PageLoader label="Loading team leader view…" />
      </Sidebar>
    );
  }

  return (
    <Sidebar title="Team Leader">
      <section className="space-y-6">
        <div className="bg-seha-500 text-white rounded-xl p-6 shadow-panel flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${incident?.isMajor ? 'bg-red-500' : 'bg-neutral-500'}`}>
                {incident ? (incident.isMajor ? 'Major Incident' : 'Active Incident') : 'No Active Incident'}
              </span>
              {incident && (
                <span className="text-seha-100 text-xs font-medium tracking-wide">{incident.refCode}</span>
              )}
            </div>
            <h2 className="text-2xl font-bold tracking-tight">
              {incident ? `${incident.title} - ${incident.type} Response` : 'No active response in progress'}
            </h2>
            <p className="text-seha-100 text-sm mt-1 font-medium">
              Team Leader view with METHANE, live team GPS, distance, readiness and resources.
            </p>
          </div>
          <div className="relative z-10 flex flex-wrap gap-3">
            <Button variant="secondary" className="shadow-sm" onClick={() => setModal('teamStatus')}>
              Update Status
            </Button>
            <Button variant="inverse" onClick={() => setModal('resource')}>
              Request Resources
            </Button>
            <Button variant="inverse" onClick={() => setModal('broadcast')}>
              Broadcast Team
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 h-[470px] bg-white rounded-xl border border-neutral-200 shadow-subtle overflow-hidden relative">
            {incident ? (
              <IncidentMap
                lat={Number(incident.lat)}
                lng={Number(incident.lng)}
                details={incident.title}
                members={rawMembers}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm font-medium text-neutral-400">
                No active incident to display on the map
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle flex flex-col">
            <div className="px-5 py-4 border-b border-neutral-100">
              <h3 className="font-bold text-neutral-800 text-sm">Team Members</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {roster.length > 0 ? (
                roster.map((m) => {
                  const allReady = m.roleConfirmed
                    && EQUIPMENT_ITEMS.every((item) => !!m.equipmentChecklist[item.key])
                    && !!m.activeSince;
                  const statusDotCls = STATUS_DOT_CLS[m.status] || 'bg-yellow-500';

                  return (
                    <div
                      key={m.id}
                      className="p-2.5 rounded-lg border border-neutral-100 hover:border-neutral-200 space-y-2"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-seha-50 text-seha-700 border border-seha-100 flex items-center justify-center font-bold text-sm shrink-0">
                          {m.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-neutral-800 truncate">{m.name}</p>
                          <p className="text-xs text-neutral-500 truncate">{m.role} &bull; {m.distance} &bull; {m.battery}</p>
                        </div>
                        {allReady && (
                          <span className="text-[9px] font-bold text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5 uppercase tracking-wider shrink-0">
                            Ready
                          </span>
                        )}
                        <div className={`w-2.5 h-2.5 rounded-full ring-2 ring-white shrink-0 ${statusDotCls}`} />
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5 pl-[52px]">
                        <ChecklistChip
                          label={m.hasRole ? 'Role' : 'No role'}
                          checked={m.roleConfirmed}
                          disabled={!m.hasRole}
                          onClick={() => toggleRoleConfirmed(m)}
                        />
                        {EQUIPMENT_ITEMS.map((item) => (
                          <ChecklistChip
                            key={item.key}
                            label={item.label}
                            checked={!!m.equipmentChecklist[item.key]}
                            onClick={() => toggleEquipmentItem(m, item.key)}
                          />
                        ))}
                        {m.activeSince ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-seha-200 bg-seha-50 text-seha-700 px-2 py-0.5 text-[10px] font-semibold">
                            <Clock className="w-3 h-3" />
                            Active {formatElapsed(m.activeSince, now)}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => markActive(m)}
                            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white text-neutral-500 hover:border-seha-300 hover:text-seha-700 px-2 py-0.5 text-[10px] font-semibold"
                          >
                            <Clock className="w-3 h-3" />
                            Mark Active
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-neutral-400 text-center py-10">No team assigned</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={modal === 'teamStatus'}
        onClose={() => setModal(null)}
        title="Update Team Status"
        subtitle="Team leader field status update."
        footer={null}
      >
        <div className="space-y-3">
          {STATUS_CHOICES.map((s) => (
            <button
              key={s.key}
              className={`w-full rounded-xl p-4 font-bold text-sm border-2 text-left ${s.cls}`}
              onClick={() => closeAndToast(`Team status updated: ${s.label}`)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={modal === 'resource'}
        onClose={() => setModal(null)}
        title="Request Resources"
        subtitle="Request PPE, detector, decon, ambulance or medication support."
        confirmLabel="Send Request"
        onConfirm={() => closeAndToast('Resource request sent to command center.')}
      >
        <div className="space-y-3">
          {['Extra Ambulance Unit', 'Hazmat PPE Kit', 'PAPR Batteries', 'Decon Lane Activation'].map((item) => (
            <label
              key={item}
              className="flex items-center gap-2 border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600"
            >
              <input type="checkbox" className="accent-seha-500" />
              {item}
            </label>
          ))}
        </div>
      </Modal>

      <Modal
        open={modal === 'broadcast'}
        onClose={() => setModal(null)}
        title="Broadcast Message"
        subtitle="Push, SMS, email, voice and staff mobile app notification."
        confirmLabel="Send Broadcast"
        onConfirm={() => closeAndToast('Broadcast sent to team.')}
      >
        <div className="space-y-5">
          <div>
            <label className="form-label" htmlFor="broadcast-target">Target</label>
            <select id="broadcast-target" className="form-input">
              <option>All active staff</option>
              <option>Team leaders only</option>
              <option>Stakeholders</option>
              <option>High Management</option>
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="broadcast-message">Message</label>
            <textarea
              id="broadcast-message"
              className="form-input"
              rows={4}
              defaultValue="Maintain zone separation. Confirm decon clearance before transfer."
            />
          </div>
        </div>
      </Modal>
    </Sidebar>
  );
}
